import {
  OnboardingLiveItemFragment,
  OnboardingLiveListPeriod,
} from "../../gql/graphql.ts";
import { matchesRange, NumberRange } from "../../UI/Table/numberRange.ts";
import { DateRange, matchesDateRange } from "../../UI/Table/dateRange.ts";
import { matchesSelection } from "../../UI/Table/matchesSelection.ts";
import {
  parseDateRangeParams,
  parseKnownListParam,
  parseListParam,
  parseRangeParams,
  setDateRangeParams,
  setOrDeleteParam,
  setRangeParams,
  TableSort,
} from "../../UI/Table/listUrlParams.ts";
import { onboardingLiveTimezone } from "../onboardingLiveTime.ts";

export const ONBOARDING_LIVES_SORT_FIELDS = [
  "startUtc",
  "userGroup",
  "conductor",
  "enrolled",
  "attendees",
  "feedback",
] as const;

export type OnboardingLivesSortField =
  (typeof ONBOARDING_LIVES_SORT_FIELDS)[number];

export type OnboardingLivesSort = TableSort<OnboardingLivesSortField>;

const UPCOMING_LIVES_DEFAULT_SORT: OnboardingLivesSort = {
  sortBy: "startUtc",
  sortDirection: "asc",
};

const PAST_LIVES_DEFAULT_SORT: OnboardingLivesSort = {
  sortBy: "startUtc",
  sortDirection: "desc",
};

export function defaultOnboardingLivesSortOf(
  period: OnboardingLiveListPeriod,
): OnboardingLivesSort {
  return period === OnboardingLiveListPeriod.Upcoming
    ? UPCOMING_LIVES_DEFAULT_SORT
    : PAST_LIVES_DEFAULT_SORT;
}

export const FEEDBACK_STATES = ["missing", "sent"] as const;

export type FeedbackState = (typeof FEEDBACK_STATES)[number];

export const feedbackStateLabels: Record<FeedbackState, string> = {
  missing: "Mancante",
  sent: "Inviato",
};

export function feedbackStateOf(
  live: OnboardingLiveItemFragment,
): FeedbackState {
  return live.feedback ? "sent" : "missing";
}

export type OnboardingLivesFilters = {
  date: DateRange;
  userGroupIds: string[];
  teacherIds: string[];
  enrolled: NumberRange;
  attendees: NumberRange;
  feedbackStates: FeedbackState[];
};

export function parseOnboardingLivesFilters(
  params: URLSearchParams,
): OnboardingLivesFilters {
  return {
    date: parseDateRangeParams(params),
    userGroupIds: parseListParam(params.get("groups")),
    teacherIds: parseListParam(params.get("conductors")),
    enrolled: parseRangeParams(params, "enrolled"),
    attendees: parseRangeParams(params, "attendees"),
    feedbackStates: parseKnownListParam(
      params.get("feedback"),
      FEEDBACK_STATES,
    ),
  };
}

export function writeOnboardingLivesFilters(
  params: URLSearchParams,
  filters: OnboardingLivesFilters,
): void {
  setDateRangeParams(params, filters.date);
  setOrDeleteParam(params, "groups", filters.userGroupIds.join(","));
  setOrDeleteParam(params, "conductors", filters.teacherIds.join(","));
  setRangeParams(params, "enrolled", filters.enrolled);
  setRangeParams(params, "attendees", filters.attendees);
  setOrDeleteParam(params, "feedback", filters.feedbackStates.join(","));
}

export function matchesOnboardingLivesFilters(
  live: OnboardingLiveItemFragment,
  filters: OnboardingLivesFilters,
): boolean {
  return (
    matchesDateRange(live.startUtc, filters.date, onboardingLiveTimezone) &&
    matchesSelection(live.userGroupId, filters.userGroupIds) &&
    matchesSelection(live.teacherId, filters.teacherIds) &&
    matchesRange(live.participantsCount, filters.enrolled) &&
    matchesRange(live.feedback?.participantsCount, filters.attendees) &&
    matchesSelection(feedbackStateOf(live), filters.feedbackStates)
  );
}
